import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { cn, initials } from '@/lib/utils'
import type { Tables } from '@/types/database'

type ProfileLike = Pick<Tables<'profiles'>, 'full_name' | 'avatar_url'> | null | undefined

interface UserAvatarProps {
  profile: ProfileLike
  className?: string
  showTooltip?: boolean
}

export function UserAvatar({ profile, className, showTooltip = true }: UserAvatarProps) {
  const avatar = (
    <Avatar className={cn('h-8 w-8', className)}>
      {profile?.avatar_url && (
        <AvatarImage src={profile.avatar_url} alt={profile.full_name ?? ''} />
      )}
      <AvatarFallback>{initials(profile?.full_name)}</AvatarFallback>
    </Avatar>
  )
  if (!showTooltip || !profile?.full_name) return avatar
  return (
    <Tooltip>
      <TooltipTrigger asChild>{avatar}</TooltipTrigger>
      <TooltipContent>{profile.full_name}</TooltipContent>
    </Tooltip>
  )
}

interface AvatarGroupProps {
  profiles: ProfileLike[]
  max?: number
  className?: string
}

export function AvatarGroup({ profiles, max = 4, className }: AvatarGroupProps) {
  const shown = profiles.filter(Boolean).slice(0, max)
  const rest = Math.max(0, profiles.filter(Boolean).length - max)
  return (
    <div className={cn('flex -space-x-2', className)}>
      {shown.map((p, i) => (
        <UserAvatar key={i} profile={p} className="h-7 w-7 ring-2 ring-background" />
      ))}
      {rest > 0 && (
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-muted text-[10px] font-semibold ring-2 ring-background">
          +{rest}
        </span>
      )}
    </div>
  )
}
